/**
 * Centralized registry for all pricing costs
 * Replaces scattered individual properties like costAcrylicDoor, costGlassInsideDoor, etc.
 *
 * Features:
 * - Unified hierarchy: doors/materials/component/accessory/labor/special
 * - Automatic dirty-flag tracking for cache invalidation
 * - Batch operations for efficient bulk additions
 * - Persistence support (toJSON/fromJSON)
 */

import { generateRegistryTemplate } from './pricingStructure';
import { validateStructure } from './pricingStructure';
// import * as Sentry from '@sentry/react';

export default class PricingRegistry {
  /**
   * Initialize pricing registry with optional metadata
   * @param {object} options - Configuration options
   * @param {object} options.metadata - Optional metadata to attach to registry
   */
  constructor(options = {}) {
    this.isDirty = false;
    this.totalsCached = null;
    this.batchMode = false;
    this.batchQueue = [];
    this.createdAt = new Date();
    this.metadata = options.metadata || {};
    this.reset();
  }

  /**
   * Reset registry to initial empty state
   * Clears all cost data while preserving structure
   * Categories: doors, materials, component, accessory, labor, special
   */
  reset() {
    // DÙNG: Tự động tạo structure từ schema
    this.registry = generateRegistryTemplate([]); // [] = giá trị mặc định
    this.invalidateCache();
  }

  /**
   * Add a component cost to registry
   * @param {string} category - Main category (doors, materials, component, accessory, labor, special)
   * @param {string} type - type (acrylic, glassInside, etc.)
   * @param {string} subtype - subtype (acrylic, glassInside, etc.)
   * @param {number|object} cost - Cost value or object with cost
   */
  addCost(category, type, cost, subtype) {
    if (this.batchMode) {
      this.batchQueue.push({ category, type, cost, subtype });
      return this;
    }

    if (!this.registry[category]) {
      this.registry[category] = {};
    }

    if (!this.registry[category][type]) {
      this.registry[category][type] = subtype ? {} : [];
    }

    if (subtype) {
      if (!this.registry[category][type][subtype]) {
        this.registry[category][type][subtype] = [];
      }

      const target = this.registry[category][type][subtype];
      this.addToTarget(target, cost);
    } else {
      const target = this.registry[category][type];
      this.addToTarget(target, cost);
    }

    this.invalidateCache();
    return this;
  }

  /**
   * Helper to add cost to target (array or object)
   * Handles arrays (push), objects (merge), and numbers (sum)
   * @param {array|object|number} target - Destination to add cost to
   * @param {number|object} cost - Cost value or object
   * @returns {undefined}
   */
  addToTarget(target, cost) {
    if (Array.isArray(target)) {
      target.push(cost);
    } else if (
      typeof target === 'object' &&
      target !== null &&
      typeof cost === 'object' &&
      cost !== null
    ) {
      Object.assign(target, cost);
    } else if (typeof cost === 'number') {
      return (typeof target === 'number' ? target : 0) + cost;
    } else {
      return cost;
    }
  }

  /**
   * Start batch mode - queue additions until executeBatch() called
   * Prevents cache invalidation on every addition
   * @returns {this} For method chaining
   */
  startBatch() {
    this.batchMode = true;
    this.batchQueue = [];
    return this;
  }

  /**
   * Execute all batched additions and invalidate cache once
   * @returns {this} For method chaining
   */
  executeBatch() {
    this.batchMode = false;
    for (const { category, type, cost, subtype } of this.batchQueue) {
      this.addCost(category, type, cost, subtype);
    }
    this.batchQueue = [];
    return this;
  }

  /**
   * Add multiple costs in batch (convenience method)
   * @param {array} items - Array of { category, type, cost, subtype }
   * @returns {this} For method chaining
   */
  addCosts(items) {
    this.startBatch();
    for (const item of items) {
      this.addCost(item.category, item.type, item.cost, item.subtype);
    }
    this.executeBatch();
    return this;
  }

  /**
   * Mark cache as invalid (call after modifications)
   */
  invalidateCache() {
    this.isDirty = true;
    this.totalsCached = null;
  }

  /**
   * Set cached totals (used by calculator after aggregation)
   * @param {object} totals - Aggregated totals
   */
  setCachedTotals(totals) {
    this.totalsCached = totals;
    this.isDirty = false;
  }

  /**
   * Get cached totals if available
   * @returns {object|null} Cached totals or null if dirty
   */
  getCachedTotals() {
    return this.isDirty ? null : this.totalsCached;
  }

  /**
   * Check if registry has unsaved changes
   * @returns {boolean} True if dirty
   */
  hasChanges() {
    return this.isDirty;
  }

  /**
   * Register a door type dynamically
   * @param {string} doorType - Door type name
   * @param {array} costs - Cost items array
   */
  registerDoorType(doorType, costs = []) {
    if (!this.registry.doors[doorType]) {
      this.registry.doors[doorType] = [];
    }
    this.registry.doors[doorType] = costs;
  }

  /**
   * Get all costs in a category
   * @param {string} category - Category name
   * @returns {object} Costs in category
   */
  getCategory(category) {
    return this.registry[category] || {};
  }

  /**
   * Get specific subcategory costs
   * @param {string} category - Category name
   * @param {string} type - Subcategory name
   * @returns {number|object|array} Costs
   */
  getCost(category, type) {
    return this.registry[category]?.[type] || 0;
  }

  /**
   * Get entire registry
   * @returns {object} Complete registry
   */
  getRegistry() {
    return this.registry;
  }

  /**
   * Export for debugging/logging
   * @returns {string} JSON representation
   */
  toJSON() {
    return JSON.stringify(this.registry, null, 2);
  }

  /**
   * Serialize for persistence to database/localStorage
   * @returns {object} Persistable representation
   */
  toPersistable() {
    return {
      version: 1,
      createdAt: this.createdAt,
      modifiedAt: new Date(),
      metadata: this.metadata,
      registry: this.registry,
    };
  }

  /**
   * Deserialize from persistence (database/localStorage)
   * @param {object} data - Persistable object
   * @returns {this} For method chaining
   */
  static fromPersistable(data) {
    if (!data || data.version !== 1) {
      // console.warn('Invalid persistable data version');
      return new PricingRegistry();
    }

    const instance = new PricingRegistry({
      metadata: data.metadata,
    });
    instance.registry = data.registry;
    instance.createdAt = new Date(data.createdAt);
    instance.invalidateCache();

    try {
      validateStructure(instance.getRegistry(), 'PricingRegistry');
      return instance;
    } catch (error) {
      console.error('Error occurred while validating persistable data:', error);
      // Sentry.captureMessage('Validation error from persistable data', {
      //   level: 'error',
      //   extra: {
      //     errorMessage: error.message,
      //     stack: error.stack,
      //     data,
      //   },
      // });
      return null;
    }
  }

  /**
   * Get registry statistics for debugging
   * @returns {object} Stats about registry contents
   */
  getStats() {
    const countItems = obj => {
      let count = 0;
      for (const key in obj) {
        const val = obj[key];
        if (Array.isArray(val)) count += val.length;
        else if (typeof val === 'object' && val !== null)
          count += countItems(val);
        else if (typeof val === 'number' && val !== 0) count += 1;
      }
      return count;
    };

    return {
      totalItems: countItems(this.registry),
      isDirty: this.isDirty,
      hasCachedTotals: this.totalsCached !== null,
      createdAt: this.createdAt,
      categories: Object.keys(this.registry),
    };
  }
}
